import {
  ICreateInvoicePayload,
  IInvoiceRecord,
  EQuantityUnit,
  EMonetaryAmountBy,
  ECurrency,
  EPaymentMethod,
  TPaymentMethodCreate,
  EEmailPermissionLevel,
  IEmailRecipient,
  IPaymentManualEntryCreate,
} from "../interfaces/invoice.interfaces";
import type { JobEventForInvoice } from "../../../jobs/jobs-details/events/board/context";
import { InvoiceCalculations } from "../shared/types";
import { EmailRecipient } from "../EmailRecipients/types";

/**
 * Maps frontend invoice data to backend format
 * All heavy logic stays in backend - this just formats the data
 */
interface PaymentMethodData {
  method: "online" | "manual"; // Accept both for backward compatibility, but only "online" is used
  selectedPayments: string[];
  referenceNumber: string;
}

const BANK_TRANSFER_LOCATION_KEY = "default";
const getBankTransferStorageKey = (locationKey: string) => `bankTransfer.details.${locationKey}`;

type StoredBankTransfer = Partial<{
  accountHolderName: string;
  accountNumber: string | number;
  routingNumber: string | number;
}>;

const readBankTransferFromStorage = (locationKey = BANK_TRANSFER_LOCATION_KEY): StoredBankTransfer | null => {
  try {
    const raw = localStorage.getItem(getBankTransferStorageKey(locationKey));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredBankTransfer | null;
    if (!parsed || typeof parsed !== "object") return null;

    return parsed;
  } catch {
    return null;
  }
};

export const mapInvoiceDataToBackend = (
  scopeOfWorkUid: string,
  jobEvents: JobEventForInvoice[],
  calculations: InvoiceCalculations,
  paymentMethod: PaymentMethodData,
  emailRecipients: EmailRecipient[],
  notes: string | null,
  currency: ECurrency = ECurrency.USD,
  sendEmail: boolean = true,
  markAsPaid: boolean = false,
  manualEntryNotes?: string,
  manualEntryTransactionId?: string,
  identifierId?: string,
  depositCredit?: { parentDepositInvoiceUid: string; creditedDepositAmount: number } | null,
  allowPartialPayments?: boolean,
  minimumPaymentAmount?: number,
  companyName?: string,
): ICreateInvoicePayload => {
  const filtered = jobEvents.map((e) => e.uid).filter((uid): uid is string => Boolean(uid));
  const uniqueUids = filtered.filter((uid, index) => filtered.indexOf(uid) === index);
  if (uniqueUids.length === 0) {
    throw new Error("At least one job event UID is required");
  }

  // Helper to create IInvoiceRecord from a cost value
  const createInvoiceRecord = (cost: number, alias?: string): IInvoiceRecord => ({
    quantity: {
      value: 1,
      unit: EQuantityUnit.PER_ITEM,
    },
    price: {
      value: cost,
      by: EMonetaryAmountBy.UNIQUE,
      currency,
    },
    total: {
      value: cost,
      by: EMonetaryAmountBy.UNIQUE,
      currency,
    },
    ...(alias ? { alias } : {}),
  });

  // Map payment methods - use Create type (without transaction fields)
  // Transaction fields are populated by webhooks after payment is completed
  const paymentMethods: TPaymentMethodCreate[] = [];

  // Process all selected payment methods (PayPal, Stripe, or Bank Transfer)
  // Uses 'provider' field to match backend interface
  paymentMethod.selectedPayments.forEach((paymentId) => {
    const id = paymentId.toLowerCase();

    if (id === "paypal") {
      paymentMethods.push({ provider: EPaymentMethod.PAYPAL });
      return;
    }

    if (id === "stripe") {
      paymentMethods.push({ provider: EPaymentMethod.STRIPE });
      return;
    }

    if (id === "bank_transfer") {
      const stored = readBankTransferFromStorage();

      // If bank transfer is selected, prefer real stored values.
      // Fallbacks remain to satisfy backend validator.
      const accountHolderName = String(stored?.accountHolderName ?? "").trim() || "Bank Transfer";

      const routingNumberRaw = stored?.routingNumber ?? paymentMethod.referenceNumber ?? "";
      const routingNumber = String(routingNumberRaw).trim() || "N/A";

      const accountNumberRaw = stored?.accountNumber ?? "";
      const accountNumber = String(accountNumberRaw).trim() || undefined;

      paymentMethods.push({
        provider: EPaymentMethod.BANK_TRANSFER,
        bankTransferInfo: {
          routingNumber,
          accountHolderName,
          ...(accountNumber ? { accountNumber } : {}),
        },
      });

      return;
    }
  });

  // If markAsPaid is true, add MANUAL_ENTRY as a payment method
  if (markAsPaid) {
    const manualEntry: IPaymentManualEntryCreate = {
      provider: EPaymentMethod.MANUAL_ENTRY,
    };

    if (manualEntryNotes) {
      manualEntry.notes = manualEntryNotes;
    }

    if (manualEntryTransactionId) {
      manualEntry.transactionId = manualEntryTransactionId;
    }

    paymentMethods.push(manualEntry);
  }

  // Map email recipients
  const mappedEmailRecipients: IEmailRecipient[] = emailRecipients.map((recipient) => ({
    email: recipient.email,
    permissionLevel: recipient.permissionLevel === "Can Pay" ? EEmailPermissionLevel.CAN_PAY : EEmailPermissionLevel.CAN_VIEW,
    isLocked: recipient.isLocked,
  }));

  return {
    scopeOfWorkUid,
    jobEventUid: uniqueUids[0],
    billingJobEvents: uniqueUids.map((uid) => ({ uid })),
    companyName,
    identifierId: identifierId || undefined, // Invoice identifier for traceability
    pricingDetail: calculations.pricingDetail
      ? {
          ...calculations.pricingDetail,
          minimumPaymentAmount: minimumPaymentAmount !== undefined ? minimumPaymentAmount : undefined,
          discounts: calculations.discounts ?? calculations.pricingDetail.discounts,
          extraLineItems: calculations.extraLineItems ?? calculations.pricingDetail.extraLineItems,
          serviceFees:
            calculations.pricingDetail.serviceFees && calculations.pricingDetail.serviceFees.length > 0
              ? calculations.pricingDetail.serviceFees
              : calculations.serviceFeeAmount > 0
              ? [
                  {
                    ...createInvoiceRecord(calculations.serviceFeeAmount, calculations.serviceFeeLabel),
                    kind: "amount" as const,
                    value: calculations.serviceFeeAmount,
                    appliesTo: { type: "invoice" as const },
                  },
                ]
              : [],
        }
      : undefined,
    // For now, we'll use the total cost for all cost fields
    // The backend can recalculate if needed, but we send what the client sees
    mainAreasCost: createInvoiceRecord(calculations.subtotal),
    otherAreasCost: createInvoiceRecord(0), // Can be calculated from scope of work
    additionalObjectsCost: createInvoiceRecord(0), // Can be calculated from scope of work
    specialRequestsCost: createInvoiceRecord(0), // Can be calculated from scope of work
    increasePercentageByFrequency: createInvoiceRecord(calculations.serviceFeeAmount, calculations.serviceFeeLabel),
    totalCost: createInvoiceRecord(calculations.total),
    paymentMethods,
    emailRecipients: mappedEmailRecipients.length > 0 ? mappedEmailRecipients : undefined,
    notes: notes || null,
    sendEmail: sendEmail !== false, // Default to true if not explicitly false
    allowPartialPayments: allowPartialPayments !== undefined ? allowPartialPayments : undefined,
    ...(depositCredit &&
      depositCredit.creditedDepositAmount > 0 && {
        parentDepositInvoiceUid: depositCredit.parentDepositInvoiceUid,
        creditedDepositAmount: depositCredit.creditedDepositAmount,
      }),
  };
};
